import type { Editor } from '@tiptap/core'
import { isAllowedUri } from '@tiptap/extension-link'
import type { MarkType } from '@tiptap/pm/model'
import type { EditorState, Transaction } from '@tiptap/pm/state'
import * as linkify from 'linkifyjs'

type PendingAutolinkOptions = {
    type: MarkType
    defaultProtocol?: string
    validate?: (url: string) => boolean
    shouldAutoLink?: (url: string) => boolean
}

// Adds link marks to URLs the autolink plugin has not linked yet. Autolink only
// fires once whitespace follows a URL, so one typed last is otherwise posted
// as plain text even though its embed is found. Returns null when none.
export const addPendingAutolinks = (
    state: EditorState,
    { type, defaultProtocol = 'http', validate, shouldAutoLink }: PendingAutolinkOptions,
): Transaction | null => {
    const { doc, schema } = state
    const codeMark = schema.marks.code
    const tr = state.tr

    doc.descendants((node, pos) => {
        if (!node.isTextblock) return true
        if (node.type.spec.code) return false

        // Preserve document offsets across inline nodes, including action tags
        // whose nodeSize includes content and opening/closing positions. Spaces
        // also prevent URLs from swallowing mentions or crossing hard breaks.
        let text = ''
        node.forEach(child => {
            text += child.isText ? child.text : ' '.repeat(child.nodeSize)
        })
        linkify.find(text, 'url', { defaultProtocol }).forEach(link => {
            if (!link.isLink) return
            const from = pos + 1 + link.start
            const to = pos + 1 + link.end
            if (doc.rangeHasMark(from, to, type)) return
            if (codeMark && doc.rangeHasMark(from, to, codeMark)) return
            if (validate && !validate(link.value)) return
            if (shouldAutoLink && !shouldAutoLink(link.value)) return
            tr.addMark(from, to, type.create({ href: link.href }))
        })
        return false
    })

    return tr.steps.length ? tr : null
}

// Applies addPendingAutolinks with the editor's own link rules. Composers call
// this before reading content to submit. Returns whether anything was linked.
export const commitPendingAutolinks = (editor: Editor | null | undefined): boolean => {
    if (!editor?.view || editor.isDestroyed) return false
    const type = editor.schema?.marks.link
    const link = editor.extensionManager?.extensions.find(extension => extension.name === 'link')
    if (!type || !link || link.options.autolink === false) return false

    const { defaultProtocol, protocols, isAllowedUri: allowUri, shouldAutoLink } = link.options
    const tr = addPendingAutolinks(editor.state, {
        type,
        defaultProtocol,
        validate: url =>
            allowUri(url, {
                defaultValidate: (href: string) => !!isAllowedUri(href, protocols),
                protocols,
                defaultProtocol,
            }),
        shouldAutoLink,
    })
    if (!tr) return false
    editor.view.dispatch(tr.setMeta('addToHistory', false))
    return true
}
